import type { AxiosError } from 'axios';

const CONFLICT_STATUS = 409;
// Other 409s (e.g. a patient not linked to DM) carry BAD_REQUEST and stay plain errors.
const CONFLICT_CODE = 'CONFLICT';

// The request no longer fits the server's state; callers refetch before offering it again.
export class ApiConflictError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ApiConflictError';
    }
}

function extractApiErrorMessage(error: unknown): string | undefined {
    if (typeof error === 'string') return error;
    if (
        error !== null &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
    ) {
        return (error as { message: string }).message;
    }
    return undefined;
}

/**
 * Turn an unknown thrown value from an `apiV2.*()` call into a clean `Error`
 */
export function toApiError(err: unknown): Error {
    const axiosError = err as AxiosError<{
        success: boolean;
        error: string | { code?: string; message?: string };
    }>;
    const responseBody = axiosError.response?.data;
    const message =
        extractApiErrorMessage(responseBody?.error) ??
        (err instanceof Error ? err.message : 'An unexpected error occurred.');
    const envelopeError = responseBody?.error;
    const code =
        envelopeError && typeof envelopeError === 'object' ? envelopeError.code : undefined;
    if (axiosError.response?.status === CONFLICT_STATUS && code === CONFLICT_CODE) {
        return new ApiConflictError(message);
    }
    return new Error(message);
}
